import React, { FC } from 'react'

import styled from 'styled-components'
import { useIntercom } from 'react-use-intercom'

import { MAX_MOBILE_WIDTH, MAX_TABLET_WIDTH } from 'src/Styled/variables'
import { useBudgetRange } from 'src/Redux/spotifyBudgetRangeApi'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
import { getIsManualMatchingCampaign } from 'src/Hooks/Experiments/useManualMatchingExperiment'

import { SpotifyScheduleTrackFile } from './SpotifyScheduleTrackFile'
import { SpotifyScheduleGenres } from './SpotifyScheduleGenres'
import { SpotifyScheduleLanguage } from './SpotifyScheduleLanguage'
import { SpotifyScheduleAiUsage } from './SpotifyScheduleAiUsage'
import { SpotifyScheduleTrackNote } from './SpotifyScheduleTrackNote'
import { LowReachCuratorsErrorExperiment } from './LowReachCuratorsErrorExperiment'
import { LowReachCuratorsError } from './LowReachCuratorsError'
import { SpotifyScheduleReleaseDate } from './SpotifyScheduleReleaseDate'
import { useAreMatchedCuratorsBelowMinBudget } from './useAreMatchedCuratorsBelowMinBudget'

const ParametersColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex: 1 0 0;

  @media screen and (max-width: ${MAX_TABLET_WIDTH}) {
    width: 50%;
  }

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    width: 100%;
  }
`

const Container = styled.div`
  display: flex;
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};
`

const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`

interface PreReleaseParametersProps {
  releaseDate: string | null
  trackFile: Parameters<typeof SpotifyScheduleTrackFile>[0]['trackFile']
}

const renderPreReleaseParameters = ({
  releaseDate,
  trackFile,
}: PreReleaseParametersProps): React.ReactNode => (
  <Container>
    <Title>Set release info</Title>
    <SpotifyScheduleTrackFile trackFile={trackFile} />
    <SpotifyScheduleReleaseDate releaseDate={releaseDate} />
  </Container>
)

const getShouldShowReachError = (
  isManualMatching: boolean,
  hasSelectedLanguagesWithoutAny: boolean,
  isLowReachError: boolean,
  isError: boolean,
): boolean =>
  !isManualMatching &&
  hasSelectedLanguagesWithoutAny &&
  (isLowReachError || isError)

const renderReachError = (
  isLowReachError: boolean,
  onContactSupportClick: () => void,
): React.ReactNode => {
  if (isLowReachError) {
    return <LowReachCuratorsErrorExperiment />
  }
  return <LowReachCuratorsError onContactSupportClick={onContactSupportClick} />
}

export const SpotifyTrackParameters: FC = () => {
  const { show } = useIntercom()
  const {
    campaignDetails,
    hasSelectedLanguagesWithoutAny,
    matchingType,
    isPreRelease,
    trackFile,
    releaseDate,
  } = useSpotifyCampaignStatus()
  const { isLowReachError, isError } = useBudgetRange()

  const genres = campaignDetails?.genres || []
  const languages = campaignDetails?.languages || []
  const aiUsage = campaignDetails?.aiUsage ?? null
  const isManualMatching =
    !isPreRelease && getIsManualMatchingCampaign(matchingType)
  const showError = getShouldShowReachError(
    isManualMatching,
    hasSelectedLanguagesWithoutAny,
    isLowReachError,
    isError,
  )
  const error = renderReachError(isLowReachError, show)
  const areMatchedCuratorsBelowMinBudget =
    useAreMatchedCuratorsBelowMinBudget(isManualMatching)

  return (
    <ParametersColumn>
      {isPreRelease && renderPreReleaseParameters({ releaseDate, trackFile })}
      <Container>
        <Title>Choose track parameters</Title>
        <SpotifyScheduleGenres genres={genres} />
        <SpotifyScheduleLanguage languages={languages} />
        {areMatchedCuratorsBelowMinBudget && (
          <LowReachCuratorsError onContactSupportClick={show} />
        )}
        <SpotifyScheduleAiUsage aiUsage={aiUsage} />
        <SpotifyScheduleTrackNote />
        {showError && error}
      </Container>
    </ParametersColumn>
  )
}
